<template>
  <div class="field-editor" ref="viewerContainer">
    <!-- Toolbar -->
    <div class="field-editor__toolbar">
      <div class="field-editor__toolbar-left">
        <span class="field-editor__page-info">
          Página {{ currentPage }} de {{ totalPages }}
        </span>
      </div>
      <div class="field-editor__toolbar-center">
        <button
          class="field-editor__btn"
          @click="prevPage"
          :disabled="currentPage <= 1"
        >
          <i class="el-icon-arrow-left"></i>
        </button>
        <button
          class="field-editor__btn"
          @click="nextPage"
          :disabled="currentPage >= totalPages"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
        <button
          class="field-editor__btn"
          @click="zoomOut"
          :disabled="scale <= 0.5"
        >
          <i class="el-icon-zoom-out"></i>
        </button>
        <span class="field-editor__zoom">{{ Math.round(scale * 100) }}%</span>
        <button
          class="field-editor__btn"
          @click="zoomIn"
          :disabled="scale >= 3"
        >
          <i class="el-icon-zoom-in"></i>
        </button>
      </div>
      <div class="field-editor__toolbar-right">
        <button
          class="field-editor__add-btn"
          :class="{ 'field-editor__add-btn--active': placingMode }"
          @click="togglePlacing"
        >
          <i class="el-icon-circle-plus-outline"></i>
          {{ placingMode ? "Click en el documento…" : "Agregar campo" }}
        </button>
      </div>
    </div>

    <!-- PDF Canvas Area -->
    <div class="field-editor__canvas-area" ref="canvasArea">
      <div
        class="field-editor__wrapper"
        v-if="loaded"
        :style="wrapperStyle"
        :class="{ 'field-editor__wrapper--placing': placingMode }"
        @click.self="onCanvasClick"
      >
        <canvas ref="pdfCanvas" @click="onCanvasClick"></canvas>

        <div
          v-for="field in currentPageFields"
          :key="field.id"
          class="field-box"
          :class="{ 'field-box--active': activeFieldId === field.id }"
          :style="fieldStyle(field)"
          @mousedown.stop="activeFieldId = field.id"
        >
          <div
            class="field-box__handle"
            title="Mover"
            @mousedown.stop.prevent="startDrag(field, $event)"
            @touchstart.stop.prevent="startDragTouch(field, $event)"
          >
            <i class="el-icon-rank"></i>
          </div>
          <textarea
            :ref="'textarea_' + field.id"
            class="field-box__input"
            v-model="field.value"
            placeholder="Escribe aquí…"
            @mousedown.stop
            @input="onFieldInput(field, $event)"
          />
          <button
            class="field-box__remove"
            title="Eliminar campo"
            @mousedown.stop
            @click.stop="removeField(field.id)"
          >
            <i class="el-icon-close"></i>
          </button>
          <div
            class="field-box__resize"
            @mousedown.stop.prevent="startResize(field, $event)"
            @touchstart.stop.prevent="startResizeTouch(field, $event)"
          ></div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="field-editor__loading">
        <i class="el-icon-loading"></i>
        <span>Cargando PDF...</span>
      </div>

      <!-- Error -->
      <div v-if="error" class="field-editor__error">
        <i class="el-icon-warning-outline"></i>
        <span>{{ error }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PdfFieldEditor",
  props: {
    // PDF source — either ArrayBuffer or URL
    pdfData: { type: [ArrayBuffer, Uint8Array, String], default: null },
    // Initial field definitions: [{id, page, x, y, width, height, value}]
    initialFields: { type: Array, default: () => [] },
    initialPage: { type: Number, default: 1 },
    initialScale: { type: Number, default: 1.0 }
  },
  data() {
    return {
      pdfDoc: null,
      currentPage: this.initialPage,
      totalPages: 0,
      scale: this.initialScale,
      loaded: false,
      loading: false,
      error: null,
      canvasWidth: 0,
      canvasHeight: 0,
      pdfjsLib: null,

      fields: this.initialFields.map(f => ({ ...f })),
      placingMode: false,
      activeFieldId: null,

      // Drag/resize state
      isDragging: false,
      isResizing: false,
      dragStart: { x: 0, y: 0 },
      dragFieldId: null
    };
  },
  computed: {
    wrapperStyle() {
      return {
        width: this.canvasWidth + "px",
        height: this.canvasHeight + "px",
        position: "relative"
      };
    },
    currentPageFields() {
      return this.fields.filter(f => (f.page || 1) === this.currentPage);
    }
  },
  watch: {
    pdfData: {
      handler(val) {
        if (val) this.loadPdf();
      },
      immediate: true
    }
  },
  methods: {
    async loadPdf() {
      this.loading = true;
      this.error = null;
      this.loaded = false;

      try {
        await this.loadPdfjsLib();

        let source;
        if (typeof this.pdfData === "string") {
          source = this.pdfData;
        } else {
          const copy = new Uint8Array(this.pdfData);
          source = { data: copy };
        }

        const loadingTask = this.pdfjsLib.getDocument(source);
        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        this.loaded = true;

        await this.$nextTick();
        await this.renderPage();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("PDF load error:", err);
        this.error = "No se pudo cargar el PDF. " + (err.message || "");
      } finally {
        this.loading = false;
      }
    },

    async loadPdfjsLib() {
      if (this.pdfjsLib) return;
      if (window.pdfjsLib) {
        this.pdfjsLib = window.pdfjsLib;
        if (!this.pdfjsLib.GlobalWorkerOptions.workerSrc) {
          this.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js";
        }
        return;
      }

      const cdnUrls = [
        {
          script:
            "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js",
          worker:
            "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js"
        },
        {
          script:
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js",
          worker:
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js"
        }
      ];

      for (const cdn of cdnUrls) {
        try {
          await this._loadScript(cdn.script);
          if (window.pdfjsLib) {
            this.pdfjsLib = window.pdfjsLib;
            this.pdfjsLib.GlobalWorkerOptions.workerSrc = cdn.worker;
            return;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("PDF.js CDN failed, trying next:", cdn.script);
        }
      }

      throw new Error("No se pudo cargar la librería PDF.");
    },

    _loadScript(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Script load failed: " + src));
        document.head.appendChild(script);
      });
    },

    async renderPage() {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(this.currentPage);
      const viewport = page.getViewport({ scale: this.scale });

      const canvas = this.$refs.pdfCanvas;
      if (!canvas) return;

      const context = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;

      canvas.width = viewport.width * dpr;
      canvas.height = viewport.height * dpr;
      canvas.style.width = viewport.width + "px";
      canvas.style.height = viewport.height + "px";

      this.canvasWidth = viewport.width;
      this.canvasHeight = viewport.height;

      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      await page.render({ canvasContext: context, viewport }).promise;

      // Existing fields on this page may hold multi-line values from a
      // previous session — make sure their box is already tall enough.
      await this.$nextTick();
      this.currentPageFields.forEach(field => this.growFieldToFit(field));
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.renderPage();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.renderPage();
      }
    },

    zoomIn() {
      this.scale = Math.min(3, this.scale + 0.2);
      this.renderPage();
    },

    zoomOut() {
      this.scale = Math.max(0.5, this.scale - 0.2);
      this.renderPage();
    },

    // ─── Add field ───────────────────────────────────────────────────
    togglePlacing() {
      this.placingMode = !this.placingMode;
    },

    onCanvasClick(e) {
      if (!this.placingMode) {
        this.activeFieldId = null;
        return;
      }

      const wrapper = this.$el.querySelector(".field-editor__wrapper");
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      const width = 25;
      const height = 6;
      const xPct = Math.max(
        0,
        Math.min(100 - width, (clickX / this.canvasWidth) * 100 - width / 2)
      );
      const yPct = Math.max(
        0,
        Math.min(100 - height, (clickY / this.canvasHeight) * 100 - height / 2)
      );

      const field = {
        id: this.generateId(),
        page: this.currentPage,
        x: Math.round(xPct * 100) / 100,
        y: Math.round(yPct * 100) / 100,
        width,
        height,
        value: ""
      };

      this.fields.push(field);
      this.activeFieldId = field.id;
      this.placingMode = false;
      this.emitChange();
    },

    removeField(id) {
      this.fields = this.fields.filter(f => f.id !== id);
      if (this.activeFieldId === id) this.activeFieldId = null;
      this.emitChange();
    },

    generateId() {
      return (
        "f" +
        Date.now().toString(36) +
        Math.random()
          .toString(36)
          .slice(2, 7)
      );
    },

    fieldStyle(field) {
      return {
        left: field.x + "%",
        top: field.y + "%",
        width: field.width + "%",
        height: field.height + "%"
      };
    },

    onFieldInput(field, e) {
      this.growFieldToFit(field, e.target);
      this.emitChange();
    },

    /**
     * Grow (never shrink) a field's height so its wrapped text always fits,
     * instead of hiding overflow — otherwise the box stays whatever size it
     * was created at while the text wraps to more lines than it can show.
     */
    growFieldToFit(field, textareaEl) {
      const el =
        textareaEl ||
        (Array.isArray(this.$refs["textarea_" + field.id])
          ? this.$refs["textarea_" + field.id][0]
          : this.$refs["textarea_" + field.id]);
      if (!el || !this.canvasHeight) return;

      const prevFlex = el.style.flex;
      const prevHeight = el.style.height;
      el.style.flex = "none";
      el.style.height = "auto";
      const needed = el.scrollHeight;
      el.style.flex = prevFlex;
      el.style.height = prevHeight;

      const neededPct = (needed / this.canvasHeight) * 100;
      if (neededPct > field.height) {
        field.height = Math.min(70, Math.round(neededPct * 100) / 100);
      }
    },

    // ─── Drag ────────────────────────────────────────────────────────
    startDrag(field, e) {
      this.isDragging = true;
      this.dragFieldId = field.id;
      this.activeFieldId = field.id;
      this.dragStart = { x: e.clientX, y: e.clientY };
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },

    startDragTouch(field, e) {
      if (!e.touches.length) return;
      this.isDragging = true;
      this.dragFieldId = field.id;
      this.activeFieldId = field.id;
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      document.addEventListener("touchmove", this.onDragTouch, {
        passive: false
      });
      document.addEventListener("touchend", this.stopDrag);
    },

    onDrag(e) {
      if (!this.isDragging) return;
      this.applyDragDelta(
        e.clientX - this.dragStart.x,
        e.clientY - this.dragStart.y
      );
      this.dragStart = { x: e.clientX, y: e.clientY };
    },

    onDragTouch(e) {
      if (!this.isDragging || !e.touches.length) return;
      e.preventDefault();
      this.applyDragDelta(
        e.touches[0].clientX - this.dragStart.x,
        e.touches[0].clientY - this.dragStart.y
      );
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    },

    applyDragDelta(dx, dy) {
      const field = this.fields.find(f => f.id === this.dragFieldId);
      if (!field) return;
      const pctX = (dx / this.canvasWidth) * 100;
      const pctY = (dy / this.canvasHeight) * 100;
      field.x = Math.max(0, Math.min(100 - field.width, field.x + pctX));
      field.y = Math.max(0, Math.min(100 - field.height, field.y + pctY));
    },

    stopDrag() {
      const wasActive = this.isDragging || this.isResizing;
      this.isDragging = false;
      this.isResizing = false;
      this.dragFieldId = null;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.onDragTouch);
      document.removeEventListener("touchmove", this.onResizeTouch);
      document.removeEventListener("touchend", this.stopDrag);
      if (wasActive) this.emitChange();
    },

    // ─── Resize ──────────────────────────────────────────────────────
    startResize(field, e) {
      this.isResizing = true;
      this.dragFieldId = field.id;
      this.activeFieldId = field.id;
      this.dragStart = { x: e.clientX, y: e.clientY };
      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopDrag);
    },

    startResizeTouch(field, e) {
      if (!e.touches.length) return;
      this.isResizing = true;
      this.dragFieldId = field.id;
      this.activeFieldId = field.id;
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      document.addEventListener("touchmove", this.onResizeTouch, {
        passive: false
      });
      document.addEventListener("touchend", this.stopDrag);
    },

    onResize(e) {
      if (!this.isResizing) return;
      this.applyResizeDelta(
        e.clientX - this.dragStart.x,
        e.clientY - this.dragStart.y
      );
      this.dragStart = { x: e.clientX, y: e.clientY };
    },

    onResizeTouch(e) {
      if (!this.isResizing || !e.touches.length) return;
      e.preventDefault();
      this.applyResizeDelta(
        e.touches[0].clientX - this.dragStart.x,
        e.touches[0].clientY - this.dragStart.y
      );
      this.dragStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    },

    applyResizeDelta(dx, dy) {
      const field = this.fields.find(f => f.id === this.dragFieldId);
      if (!field) return;
      const pctX = (dx / this.canvasWidth) * 100;
      const pctY = (dy / this.canvasHeight) * 100;
      field.width = Math.max(8, Math.min(80, field.width + pctX));
      field.height = Math.max(3, Math.min(40, field.height + pctY));
    },

    // ─── Emit ────────────────────────────────────────────────────────
    emitChange() {
      this.$emit("fields-change", this.fields);
    },

    /** Public method: get current fields */
    getFields() {
      return this.fields;
    }
  },
  beforeDestroy() {
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mousemove", this.onResize);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("touchmove", this.onDragTouch);
    document.removeEventListener("touchmove", this.onResizeTouch);
    document.removeEventListener("touchend", this.stopDrag);
  }
};
</script>

<style scoped>
.field-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f0f2f5;
  border-radius: 12px;
  overflow: hidden;
}

.field-editor__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #e4eaf6;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
}

.field-editor__page-info {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.field-editor__toolbar-center {
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-editor__btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e4eaf6;
  border-radius: 8px;
  background: #fff;
  color: #31355c;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.field-editor__btn:hover:not(:disabled) {
  background: #f7f8f9;
  border-color: #3fdbbf;
}

.field-editor__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.field-editor__zoom {
  font-size: 12px;
  color: #989aae;
  min-width: 40px;
  text-align: center;
}

.field-editor__add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #3fdbbf;
  background: #fff;
  color: #1d9e75;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.field-editor__add-btn:hover {
  background: #f0fdf9;
}

.field-editor__add-btn--active {
  background: #3fdbbf;
  color: #fff;
}

.field-editor__canvas-area {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: 20px;
}

.field-editor__wrapper {
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  background: #fff;
}

.field-editor__wrapper canvas {
  display: block;
}

.field-editor__wrapper--placing {
  cursor: crosshair;
}

.field-editor__wrapper--placing canvas {
  cursor: crosshair;
}

/* Field box */
.field-box {
  position: absolute;
  border: 2px dashed #3fdbbf;
  background: rgba(63, 219, 191, 0.08);
  border-radius: 6px;
  display: flex;
  flex-direction: row;
  z-index: 10;
  transition: border-color 0.15s, background 0.15s;
}

.field-box:hover,
.field-box--active {
  background: rgba(63, 219, 191, 0.15);
  border-color: #2bc4a8;
}

.field-box--active {
  border-style: solid;
  z-index: 11;
}

.field-box__handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  flex-shrink: 0;
  background: rgba(63, 219, 191, 0.9);
  color: #fff;
  font-size: 10px;
  cursor: grab;
  border-radius: 4px 0 0 4px;
}

.field-box__remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background: #e24b4a;
  color: #fff;
  cursor: pointer;
  font-size: 10px;
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.field-box__input {
  flex: 1;
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  font-size: 12px;
  color: #1f2937;
  padding: 4px 6px;
  outline: none;
  font-family: inherit;
  line-height: 1.3;
  overflow: hidden;
}

.field-box__resize {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 12px;
  height: 12px;
  background: #3fdbbf;
  border-radius: 3px;
  cursor: nwse-resize;
}

/* Loading & Error */
.field-editor__loading,
.field-editor__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: #989aae;
  font-size: 14px;
}

.field-editor__loading i,
.field-editor__error i {
  font-size: 32px;
}

.field-editor__error {
  color: #e74c3c;
}
</style>
